import { gql, useMutation } from '@apollo/client';
import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { formatOperationError } from '@/api/formatError';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { datadogRum } from '@datadog/browser-rum';
import { Icon } from '@/components/primitives/icon/Icon';
import { Input } from '@/components/primitives/Input';
import { SubmitButton } from '@/components/primitives/SubmitButton';
import { AutoTooltip } from '@/components/primitives/AutoTooltip';
import { SearchDropdown } from '@/components';
import { closeDialog, setDialog } from '@/redux/reducers/dialog';
import { useAppDispatch } from '@/redux/hooks';
import clsx from 'clsx';
import { useNavigate } from 'react-router-dom';
import { baseApi } from '@/hooks/api/base';
import { SidebarFloorDocument } from '@/.generated/graphql';
import { SidebarMenuSiteQuery } from './SidebarMenu';
import { useGetAllBuildingsQuery } from '@/.generated/graphql';
import { useSRZId } from '@/hooks';
import { useGetFloorQuery } from '../DetailsPanel/getFloor';

const CREATE_FLOOR_MUTATION = gql`
  mutation createFloors($floors: [CreateFloorInput!]!) {
    createFloors(floors: $floors) {
      client_id
      id: floor_id
      name
      customID
      building_id
    }
  }
`;

function ImportantNotice() {
  const dispatch = useAppDispatch();
  const [dontShow, setDontShowAgain] = useState(false);
  return (
    <div className="flex flex-col gap-5">
      <h1 className="text-xl font-semibold text-content-primary">Important notice</h1>
      <p className="text-xs text-content-primary">
        Your adjustments will <b>NOT</b> affect the Charts on the dashboard until you click on the
        &apos;Save&apos; button.
      </p>
      <label className="flex gap-2 text-xs text-content-secondary">
        <input
          type="checkbox"
          value=""
          checked={dontShow}
          onChange={() => setDontShowAgain((c) => !c)}
        />
        Do not show this message again
      </label>
      <div className="flex justify-end gap-4">
        <button
          className="flex justify-center rounded-md bg-btn-action-bg px-5 py-2 align-middle text-white hover:bg-gray-fixed-700 active:bg-gray-fixed-800"
          onClick={() => {
            if (dontShow) {
              window.localStorage.setItem('studioImportantNotice', 'true');
            } else {
              window.localStorage.removeItem('studioImportantNotice');
            }
            dispatch(closeDialog());
          }}
        >
          Okay
        </button>
      </div>
    </div>
  );
}

export function CreateFloorDialog() {
  const dispatch = useAppDispatch();
  const [createFloors] = useMutation(CREATE_FLOOR_MUTATION, {
    refetchQueries: [{ query: SidebarFloorDocument }, { query: SidebarMenuSiteQuery }],
  });
  const { data: buildingsData } = useGetAllBuildingsQuery();
  const [loading, setLoading] = useState(false);
  const {
    register,
    handleSubmit,
    setValue,
    setError,
    watch,
    clearErrors,
    formState: { isValid, isDirty, errors },
  } = useForm({
    defaultValues: {
      name: '',
      customID: '',
      buildingId: '',
    },
    mode: 'onBlur',
  });

  const { buildingId: currentBuilding, spaceId } = useSRZId();
  const { data: space, loading: spaceLoading } = useGetFloorQuery({ floorId: spaceId });

  const initialSetupDone = React.useRef(false);
  const initialBuildingId = currentBuilding || space?.building.id;

  useEffect(() => {
    if (initialBuildingId && !initialSetupDone.current) {
      setValue('buildingId', initialBuildingId, { shouldDirty: true, shouldValidate: true });
      initialSetupDone.current = true;
    }
  }, [initialBuildingId, setValue]);

  const formBuildingId = watch('buildingId');
  const selectedBuilding = buildingsData?.buildings?.data.find(
    (building) => building.id === formBuildingId,
  );
  const navigate = useNavigate();
  return (
    <form
      className="flex flex-col gap-4"
      onSubmit={handleSubmit(async (values) => {
        if (!values.buildingId) {
          setError('buildingId', { type: 'required', message: "Building can't be empty." });
          return;
        }
        setLoading(true);
        datadogRum.addAction('Create floor', { source: 'studio-on-web' });
        try {
          const { data } = await createFloors({
            variables: {
              floors: [
                {
                  name: values.name,
                  buildingID: values.buildingId,
                },
              ],
            },
          });

          const id = data?.createFloors?.[0]?.id;
          const floorName = values.name;

          dispatch(baseApi.util.invalidateTags(['Space']));

          toast.success(`Floor "${floorName}" created successfully`, { duration: 5000 });

          if (id) {
            navigate({ pathname: '/studio', search: `?spaceId=${id}` });
          }

          if (!window.localStorage.getItem('studioImportantNotice')) {
            dispatch(
              setDialog({
                content: <ImportantNotice />,
              }),
            );
          } else {
            dispatch(closeDialog());
          }
        } catch (error) {
          console.error('Error creating floor:', error);
          toast.error(formatOperationError('create floor', error), { duration: 5000 });
          dispatch(closeDialog());
        } finally {
          setLoading(false);
        }
      })}
    >
      <h1 className="text-base text-content-primary">Add A New Floor</h1>
      <div className="w-full mb-3 align-top">
        <span className="text-content-primary text-sm ml-0 mb-2">Assign to a Building:</span>
        <SearchDropdown
          id="buildingDropdown"
          size="sm"
          options={buildingsData?.buildings?.data ?? []}
          optionToSearchValue={(option) => (option ? `${option.name}` : '')}
          renderOption={(option) => `${option.name}`}
          placeholder="Building Name"
          className={clsx('w-full py-2', errors.buildingId && 'border-red-500')}
          onClear={() => {
            setValue('buildingId', '', { shouldDirty: true });
            setError('buildingId', { type: 'required', message: "Building can't be empty." });
          }}
          onChange={(value) => {
            if (value) {
              setValue('buildingId', value.id, { shouldDirty: true, shouldValidate: true });
              clearErrors('buildingId');
            }
          }}
          value={selectedBuilding}
        />
        {errors.buildingId && (
          <div className="text-red-500 text-xs">{errors.buildingId.message}</div>
        )}
      </div>
      <hr className="border-t border-default my-2" />
      <div className="w-full py-2">
        <span className="text-content-primary text-sm ml-0 mb-2">Name:</span>
        <Input
          type="text"
          label=""
          wrapperClassName="w-full"
          variant="narrow"
          errorMessage={errors.name && errors.name.message}
          className={errors.name ? 'border-red-500' : ''}
          {...register('name', { required: 'Please enter a floor name.' })}
        />
      </div>
      <div className="w-full py-2">
        <span className="text-content-primary text-sm ml-0 mb-2">Custom ID:</span>
        <Input
          type="text"
          label=""
          wrapperClassName="w-full"
          variant="narrow"
          {...register('customID')}
        />
      </div>
      <div className="flex justify-end gap-4">
        <button
          type="button"
          className="rounded-md px-4 py-1 text-sm text-content-primary hover:bg-overlay-hover border border-default"
          onClick={() => dispatch(closeDialog())}
        >
          Cancel
        </button>
        <SubmitButton
          color="red"
          variant="solid"
          loading={loading}
          loadingContent="Creating"
          disabled={!isValid || !isDirty || spaceLoading}
        >
          Create Floor
        </SubmitButton>
      </div>
    </form>
  );
}

export function CreateFloorButton({ variant = 'default' }: { variant?: 'default' | 'compact' }) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const onClick = async () =>
    dispatch(
      setDialog({
        content: <CreateFloorDialog />,
        options: {
          className: 'w-[520px] rounded-[15px]',
        },
      }),
    );
  const isCompact = variant === 'compact';
  return (
    <AutoTooltip label="Create Floor">
      <button
        onClick={onClick}
        className={clsx(
          'whitespace-nowrap rounded flex items-center text-sm justify-center text-content-primary hover:bg-overlay-hover border border-default',
          variant === 'default' ? 'py-2 px-4' : '',
          isCompact ? 'py-2 px-2 mb-4' : '',
        )}
      >
        <Icon name="plus" className={clsx('w-4', { 'mr-2': !isCompact })} color="black"></Icon>
        {!isCompact && t('create')}
      </button>
    </AutoTooltip>
  );
}
